import { Injectable, inject } from '@angular/core';
import { of, switchMap, firstValueFrom, Observable, tap } from 'rxjs';
import { Play } from '@r1-compass-library/models';
import { SnackBarService, PlayService } from '@r1-compass-library/services';
import { MatDialog } from '@angular/material/dialog';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import {
    DisplayAssetDependenciesListComponent,
    DisplayAssetDependencies,
} from '@r1-compass-library/components/display-asset-dependencies-list/display-asset-dependencies-list.component';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import { LifecycleRulesValidationStateService } from '../lifecycle-rules-validation-state.service';
import { LifecycleRulesetStore } from '../store/lifecycle-ruleset.store';
import { LifecycleRulesEnableComponent } from './lifecycle-rules-enable.component';
import { EnableService, ToggleOutcome } from '@r1-compass-library/features/validation-sync';
import { rxMutation } from '@angular-architects/ngrx-toolkit';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { PlayRevalidationService } from '../../plays/play-revalidation.service';

@Injectable({
    providedIn: LifecycleRulesEnableComponent,
})
export class LifecycleRulesEnableService implements EnableService<LifecycleRuleset, Play> {
    readonly #store = inject(LifecycleRulesetStore);
    readonly #playService = inject(PlayService);
    readonly #dialog = inject(MatDialog);
    readonly #validationStateService = inject(LifecycleRulesValidationStateService);
    readonly #snackBarService = inject(SnackBarService);
    readonly #playRevalidationService = inject(PlayRevalidationService);
    readonly #lifecycleRulesValidationStateService = inject(LifecycleRulesValidationStateService);

    public enable = rxMutation<void, ToggleOutcome<Play>>({
        operation: (_: void) => {
            const isValid = this.#validationStateService.invalidSections().length === 0;
            if (!isValid) {
                return of({ shouldSetEnabled: false });
            }
            return of({ shouldSetEnabled: true });
        },
        onSuccess: res => {
            if (!res.shouldSetEnabled) {
                this.#snackBarService.openGenericSnackbar({
                    messages: [
                        'This lifecycle ruleset is invalid and cannot be enabled. Please review and resolve validation errors and try again.',
                    ],
                    verticalPosition: 'top',
                    panelClass: 'generic-snack-bar-warning',
                    showDismissButton: true,
                });
            }
        },
    });

    public disable = rxMutation<LifecycleRuleset, ToggleOutcome<Play>>({
        operation: (lifecycleRuleset: LifecycleRuleset) => {
            return this.#playService
                .search({ lifecycleRulesetId: lifecycleRuleset.id, deleted: 'False', enabled: 'True' })
                .pipe(
                    switchMap(plays => {
                        if (plays.length > 0) {
                            return of({
                                shouldSetEnabled: true,
                                dependencies: plays,
                            });
                        }
                        return of({ shouldSetEnabled: false });
                    })
                );
        },
        onSuccess: (res, args) => {
            if ((res.dependencies ?? []).length > 0) {
                this.openDependencyDialog(args.id, res.dependencies ?? []);
            }
        },
    });

    private openDependencyDialog(LifecycleRulesetId: number, plays: Play[]) {
        const dependencies: Dependency[] = plays.map(play => ({
            playId: play.id,
            name: play.name,
            assetId: LifecycleRulesetId,
        }));
        this.#dialog.open<DisplayAssetDependenciesListComponent, DisplayAssetDependencies>(
            DisplayAssetDependenciesListComponent,
            {
                width: '600px',
                disableClose: true,
                data: {
                    dependencies: dependencies,
                    dialogText: `Can't disable lifecycle ruleset because the following enabled plays depend on it:`,
                },
                autoFocus: false,
            }
        );
    }

    public async save(userSetEnabled: boolean | undefined) {
        const lifecycleRulesetId = this.#store.lifecycleRuleset()?.id;
        if (!lifecycleRulesetId || userSetEnabled === undefined) {
            return noopFormSaveMutationResult();
        }

        const wasOriginallyEnabled = this.#store.lifecycleRuleset()?.enabled ?? false;

        if (wasOriginallyEnabled && !userSetEnabled) {
            await firstValueFrom(this.invalidatePlayDependencies(lifecycleRulesetId));
        }

        const res = await this.#store.saveLifecycleRuleset({
            id: lifecycleRulesetId,
            enabled: userSetEnabled,
            valid: this.#lifecycleRulesValidationStateService.invalidSections().length === 0,
        });

        if (res.status === 'success' && !wasOriginallyEnabled && userSetEnabled) {
            await firstValueFrom(
                this.#playRevalidationService.revalidatePlaysForEntity('lifecycleRuleset', lifecycleRulesetId)
            );
        }

        return res;
    }

    private invalidatePlayDependencies(id: number): Observable<Play[]> {
        return this.#playService.search({ lifecycleRulesetId: id, deleted: 'False' }).pipe(
            switchMap(plays => {
                const playsToInvalidate = plays.filter(p => p.valid);
                if (playsToInvalidate.length === 0) {
                    return of<Play[]>([]);
                }
                const playIdsToInvalidate = playsToInvalidate.map(p => p.id);
                return this.#playService.patchPlays({ ids: playIdsToInvalidate }, { valid: false }).pipe(
                    tap(() => {
                        const dependencies: Dependency[] = playsToInvalidate.map(play => ({
                            playId: play.id,
                            name: play.name,
                            assetId: id,
                        }));
                        this.#dialog.open<DisplayAssetDependenciesListComponent, DisplayAssetDependencies>(
                            DisplayAssetDependenciesListComponent,
                            {
                                width: '600px',
                                disableClose: true,
                                data: {
                                    dependencies,
                                    dialogText: `The following play${playsToInvalidate.length > 1 ? 's were' : ' was'} updated to invalid:`,
                                    dialogTitle: 'Plays Updated',
                                    cancelButtonText: 'Close',
                                },
                                autoFocus: false,
                            }
                        );
                    })
                );
            })
        );
    }
}
